import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Toolbar
} from '@mui/material'
import { useBatchExportModelPresetsMutation } from 'apollo/configurator/mutations/BatchExportModelPresets.generated'
import { useExportModelMutation } from 'apollo/configurator/mutations/ExportModel.generated'
import { useFlushActionsCacheMutation } from 'apollo/personalization/mutations/FlushActionsCache.generated'
import { ErrorContext } from 'context/error/ErrorContext'
import { ModelContext } from 'context/model/ModelContext'
import { useContext, useState } from 'react'
import { useNotify } from 'react-admin'

interface PublishModelToolbarProps {
    modelId: number | null
}

const PublishModelToolbar = ({ modelId }: PublishModelToolbarProps) => {
    const { state, setState } = useContext(ModelContext)
    const { setErrorState } = useContext(ErrorContext)
    const notify = useNotify()
    const [publishDialogOpen, setPublishDialogOpen] = useState(false)
    const [isPublishing, setIsPublishing] = useState(false)
    const [batchExportModelPresets] = useBatchExportModelPresetsMutation()
    const [exportModel] = useExportModelMutation()
    const [flushActionsCache] = useFlushActionsCacheMutation()

    const handlePublishModelAndPresets = async () => {
        setIsPublishing(true)

        try {
            const result = await exportModel({
                variables: { id: modelId as any }
            })

            if (result.data?.configuratorExportModel) {
                await batchExportModelPresets({
                    variables: { model: modelId as any }
                })

                setState({
                    ...state,
                    hasEdited: false,
                    hasChanges: false,
                    showEditedMessage: false
                })

                await flushActionsCache()

                notify('Model and presets published successfully', { type: 'success' })
            }
        } catch (error: any) {
            setErrorState({ hasError: true, message: error.message })
            notify('Failed to publish model and presets', { type: 'error' })
        } finally {
            setIsPublishing(false)
            setPublishDialogOpen(false)
        }
    }

    if (!modelId) return null

    return (
        <>
            <Toolbar
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    backgroundColor: 'white',
                    borderTop: '1px solid #eaeaea',
                    width: 'calc(100% + 16px)',
                    marginLeft: -1
                }}
            >
                <Button
                    variant="contained"
                    color={state.hasChanges ? 'warning' : 'primary'}
                    onClick={() => setPublishDialogOpen(true)}
                    disabled={isPublishing}
                    startIcon={isPublishing ? <CircularProgress size={20} /> : undefined}
                    sx={{ minWidth: 200 }}
                >
                    {isPublishing ? 'Publishing...' : 'Publish model and presets'}
                </Button>
            </Toolbar>

            <Dialog
                open={publishDialogOpen}
                onClose={() => setPublishDialogOpen(false)}
                aria-labelledby="publish-dialog-title"
            >
                <DialogTitle id="publish-dialog-title">Publish Model and Presets</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        This will update all affected presets and publish the model. This action may
                        have non-reversible consequences. Do you want to continue?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>

                    <Button
                        onClick={handlePublishModelAndPresets}
                        variant="contained"
                        color="primary"
                        disabled={isPublishing}
                    >
                        {isPublishing ? 'Publishing...' : 'Publish'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default PublishModelToolbar
